import { Box, Grid } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { PrimaryButton } from '../../../../Buttons';
import DemographicsSetForm from './DemographicsSetForm';
import { Brand, DemographicsSet } from '../../../../../types';
import {
  createOrUpdateDemographicsSet,
  getBrandDemographicsSets,
} from '../../../../../services/assets';
import DemographicsSetTable from '../../../../Tables/AudienceList/DemographicsSetTable';
import { ConfirmationModal } from '../../../../Modal';
import { errorMessageParser } from '../../../../../utils/helpers/ToastHelper';
import { useDispatch } from 'react-redux';
import { toggleAlert } from '../../../../../redux/actions';

interface DemographicsSetProps {
  brand: Brand;
}

const DemographicsAsset: FC<DemographicsSetProps> = ({ brand }) => {
  const dispatch = useDispatch();
  const [isCreate, setIsCreate] = useState<boolean>(false);
  const [demographicsSets, setDemographicsSets] = useState<DemographicsSet[]>(
    [],
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [editDemographicsSet, setEditDemographicsSet] =
    useState<boolean>(false);
  const [editDemographicsSetId, setEditDemographicsSetId] = useState<
    string | null
  >(null);
  const [archiveDemographicsSetId, setArchiveDemographicsSetId] = useState<
    string | null
  >(null);
  const [openConfirmArchive, setOpenConfirmArchive] = useState<boolean>(false);
  const [archiveLoading, setArchiveLoading] = useState<boolean>(false);

  useEffect(() => {
    if (brand && !isCreate) getDemographicsSets();
  }, [brand, isCreate]);

  const getDemographicsSets = async () => {
    try {
      setLoading(true);

      const response = await getBrandDemographicsSets({
        brandId: brand?._id,
        archived: false,
      });

      const sortedResponse = response.data.sort((a: DemographicsSet) => {
        if (a.primary) return -1;

        return 1;
      });

      setDemographicsSets(sortedResponse);
    } catch (error: any) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOnArchive = async () => {
    try {
      setArchiveLoading(true);

      const response = await createOrUpdateDemographicsSet({
        brandId: brand?._id,
        body: { archived: true },
        demographicsSetId: archiveDemographicsSetId,
      });

      let temp: DemographicsSet[] = demographicsSets.filter(
        (demographicsSet: DemographicsSet) =>
          demographicsSet._id !== response.data._id,
      );

      setDemographicsSets(temp);
      setOpenConfirmArchive(false);

      dispatch(
        toggleAlert({
          toggle: true,
          message: 'Demographics set archived successfully',
          severity: 'success',
        }),
      );
    } catch (error: any) {
      const errorMsg = errorMessageParser(error);

      dispatch(
        toggleAlert({
          toggle: true,
          message: errorMsg,
          type: 'error',
        }),
      );
    } finally {
      setArchiveLoading(false);
    }
  };

  const handleOnEdit = (demographicsSetId: string) => {
    setIsCreate(true);
    setEditDemographicsSet(true);
    setEditDemographicsSetId(demographicsSetId);
  };

  const handleOnCloseForm = () => {
    setIsCreate(false);
    setEditDemographicsSet(false);
    setEditDemographicsSetId(null);
  };

  const handleOnClickArchive = (demographicsSetId: string) => {
    setOpenConfirmArchive(true);
    setArchiveDemographicsSetId(demographicsSetId);
  };

  const handleCloseConfirmArchive = () => {
    setOpenConfirmArchive(false);
    setArchiveDemographicsSetId(null);
  };

  return isCreate ? (
    <DemographicsSetForm
      brand={brand}
      onClose={handleOnCloseForm}
      edit={editDemographicsSet}
      demographicsSetId={editDemographicsSetId}
    />
  ) : (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <PrimaryButton
            title="Create Demographics Set"
            type="button"
            handleOnClick={() => setIsCreate(true)}
          />
        </Grid>

        <Grid item xs={12}>
          <DemographicsSetTable
            data={demographicsSets}
            loading={loading}
            onEdit={handleOnEdit}
            onArchive={handleOnClickArchive}
          />
        </Grid>
      </Grid>

      <ConfirmationModal
        title="Confirm Archive"
        subtitle="Are you sure you want to archive demographics set?"
        onClose={handleCloseConfirmArchive}
        onReject={handleCloseConfirmArchive}
        onConfirm={handleOnArchive}
        open={openConfirmArchive}
        loading={archiveLoading}
      />
    </Box>
  );
};

export default DemographicsAsset;
